import { useState } from 'preact/hooks';
import { 
  Modal, 
  Button, 
  Stack, 
  NumberInput, 
  Textarea, 
  Switch, 
  Text, 
  Divider,
  Group,
  Card,
  Alert
} from '@mantine/core';
import { IconSettings, IconInfoCircle } from '@tabler/icons-react';

export interface AIConfig {
  temperature?: number;
  topK?: number;
  systemPrompt: string;
  useAdvancedSettings: boolean;
}

interface AISettingsProps {
  config: AIConfig;
  onChange: (config: AIConfig) => void;
  onReset: () => void;
}

export const DEFAULT_AI_CONFIG: AIConfig = {
  systemPrompt: "You are a helpful AI assistant with multimodal capabilities. You can analyze images and provide clear, concise, and accurate responses. Be conversational and friendly while maintaining professionalism.",
  useAdvancedSettings: false,
};

export function AISettings({ config, onChange, onReset }: AISettingsProps) {
  const [opened, setOpened] = useState(false);
  const [localConfig, setLocalConfig] = useState<AIConfig>(config);

  const handleSave = () => {
    onChange(localConfig);
    setOpened(false);
  };

  const handleCancel = () => {
    setLocalConfig(config);
    setOpened(false);
  };

  const handleReset = () => {
    const resetConfig = { ...DEFAULT_AI_CONFIG };
    setLocalConfig(resetConfig);
    onChange(resetConfig);
    onReset();
  };

  const handleAdvancedToggle = (useAdvanced: boolean) => {
    if (useAdvanced) {
      // Enable advanced settings with default values
      setLocalConfig({
        ...localConfig,
        useAdvancedSettings: true,
        temperature: 0.7,
        topK: 3,
      });
    } else {
      // Disable advanced settings - remove temperature and topK
      const { temperature, topK, ...basicConfig } = localConfig;
      setLocalConfig({
        ...basicConfig,
        useAdvancedSettings: false,
      });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        leftSection={<IconSettings size={16} />}
        onClick={() => setOpened(true)}
      >
        AI Settings
      </Button>

      <Modal
        opened={opened}
        onClose={handleCancel}
        title={
          <Group gap="xs">
            <IconSettings size={20} />
            <Text fw={600}>AI Configuration</Text>
          </Group>
        }
        size="lg"
      >
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm">
              Configure how Chrome's AI behaves. Advanced settings must specify both temperature and topK, or neither.
            </Text>
          </Alert>

          <Card withBorder p="md">
            <Stack gap="sm">
              <Text fw={500}>System Prompt</Text>
              <Text size="xs" c="dimmed">
                This defines how the AI assistant should behave and respond.
              </Text>
              <Textarea
                value={localConfig.systemPrompt}
                onChange={(e: any) => setLocalConfig({
                  ...localConfig,
                  systemPrompt: (e.target as HTMLTextAreaElement).value
                })}
                placeholder="Enter system prompt..."
                autosize
                minRows={3}
                maxRows={6}
              />
            </Stack>
          </Card>

          <Divider />

          <Card withBorder p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <div>
                  <Text fw={500}>Advanced Settings</Text>
                  <Text size="xs" c="dimmed">
                    Enable fine-tuning of AI parameters (temperature & topK)
                  </Text>
                </div>
                <Switch
                  checked={localConfig.useAdvancedSettings}
                  onChange={(e: any) => handleAdvancedToggle((e.target as HTMLInputElement).checked)}
                />
              </Group>

              {localConfig.useAdvancedSettings && (
                <Group grow>
                  <NumberInput
                    label="Temperature"
                    description="Controls randomness (0.0-1.0)"
                    value={localConfig.temperature}
                    onChange={(value: any) => setLocalConfig({
                      ...localConfig,
                      temperature: typeof value === 'number' ? value : 0.7
                    })}
                    min={0}
                    max={1}
                    step={0.1}
                    decimalScale={1}
                  />

                  <NumberInput
                    label="Top K"
                    description="Top tokens to consider (1-40)"
                    value={localConfig.topK}
                    onChange={(value: any) => setLocalConfig({
                      ...localConfig,
                      topK: typeof value === 'number' ? value : 3
                    })}
                    min={1}
                    max={40}
                    step={1}
                  />
                </Group>
              )}
            </Stack>
          </Card>


          <Group justify="space-between">
            <Button
              variant="outline"
              color="red"
              onClick={handleReset}
            >
              Reset to Defaults
            </Button>

            <Group>
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
              >
                Save Settings
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}